// Trainer application + trainer self-service profile RPCs.
//
// Wire contract (confirmed live against the deployed Supabase project —
// exact parameter names, not guessed):
//   submit_trainer_application(p_first_name, p_last_name, p_email,
//     p_phone, p_business_name, p_training_specialty, p_location,
//     p_website_or_social, p_bio, p_certification_credentials,
//     p_approximate_active_clients, p_requested_trainer_code,
//     p_program_terms_accepted)
//   get_my_trainer_profile()  — no parameters
//   update_my_trainer_profile(p_first_name, p_last_name, p_email,
//     p_phone, p_business_name, p_training_specialty, p_location,
//     p_website_or_social, p_bio, p_certification_credentials,
//     p_approximate_active_clients)  — no code/status/terms fields;
//     the trainer cannot change those through this RPC.
//   get_current_trainer_program_terms()  — no parameters
//
// auth.uid() is resolved server-side from the session; no user_id is
// ever sent from the browser.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:trainerapp]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

export async function rpcSubmitTrainerApplication({
  firstName, lastName, email, phone, businessName, trainingSpecialty,
  location, websiteOrSocial, bio, certificationCredentials,
  approximateActiveClients, requestedTrainerCode, programTermsAccepted,
}) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('submit_trainer_application', {
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
      p_phone: phone,
      p_business_name: businessName,
      p_training_specialty: trainingSpecialty,
      p_location: location,
      p_website_or_social: websiteOrSocial,
      p_bio: bio,
      p_certification_credentials: certificationCredentials,
      p_approximate_active_clients: approximateActiveClients,
      p_requested_trainer_code: requestedTrainerCode,
      p_program_terms_accepted: programTermsAccepted,
    });
    if (error) {
      logSanitized('submit_trainer_application:error', error);
      return { ok: false, data: null, error: "We couldn't submit your application. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('submit_trainer_application:threw', err);
    return { ok: false, data: null, error: "We couldn't submit your application. Please try again." };
  }
}

export async function rpcGetMyTrainerProfile() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_my_trainer_profile');
    if (error) {
      logSanitized('get_my_trainer_profile:error', error);
      return { ok: false, data: null, error: "We couldn't load your trainer profile. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_my_trainer_profile:threw', err);
    return { ok: false, data: null, error: "We couldn't load your trainer profile. Please try again." };
  }
}

export async function rpcUpdateMyTrainerProfile({
  firstName, lastName, email, phone, businessName, trainingSpecialty,
  location, websiteOrSocial, bio, certificationCredentials,
  approximateActiveClients,
}) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('update_my_trainer_profile', {
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
      p_phone: phone,
      p_business_name: businessName,
      p_training_specialty: trainingSpecialty,
      p_location: location,
      p_website_or_social: websiteOrSocial,
      p_bio: bio,
      p_certification_credentials: certificationCredentials,
      p_approximate_active_clients: approximateActiveClients,
    });
    if (error) {
      logSanitized('update_my_trainer_profile:error', error);
      return { ok: false, data: null, error: "We couldn't save your changes. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('update_my_trainer_profile:threw', err);
    return { ok: false, data: null, error: "We couldn't save your changes. Please try again." };
  }
}

export async function rpcGetCurrentTrainerProgramTerms() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_current_trainer_program_terms');
    if (error) {
      logSanitized('get_current_trainer_program_terms:error', error);
      return { ok: false, data: null, error: "We couldn't load the Trainer Program terms. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_current_trainer_program_terms:threw', err);
    return { ok: false, data: null, error: "We couldn't load the Trainer Program terms. Please try again." };
  }
}

// get_my_trainer_referral_activity(p_start, p_end) — derives the
// trainer from auth.uid() server-side; no trainer_id/user_id param.
export async function rpcGetMyTrainerReferralActivity({ startIso, endIso }) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_my_trainer_referral_activity', {
      p_start: startIso,
      p_end: endIso,
    });
    if (error) {
      logSanitized('get_my_trainer_referral_activity:error', error);
      return { ok: false, data: null, error: "We couldn't load your referral activity. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_my_trainer_referral_activity:threw', err);
    return { ok: false, data: null, error: "We couldn't load your referral activity. Please try again." };
  }
}

// get_my_trainer_clients() — no parameters. Trainer identity is
// resolved server-side from auth.uid(); never send trainer_id,
// customer_id, or a date range.
export async function rpcGetMyTrainerClients() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'The trainer program is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_my_trainer_clients');
    if (error) {
      logSanitized('get_my_trainer_clients:error', error);
      return { ok: false, data: null, error: "We couldn't load your clients. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_my_trainer_clients:threw', err);
    return { ok: false, data: null, error: "We couldn't load your clients. Please try again." };
  }
}

// ---------------------------------------------------------------
function pickStr(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (typeof v === 'string' && v.length) return v; } return null; }
function pickNum(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (v == null || v === '') continue; const n = Number(v); if (Number.isFinite(n)) return n; } return null; }
function pickBool(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (typeof v === 'boolean') return v; } return null; }

export function readTrainerOutcome(payload) { return pickStr(payload, ['outcome']); }
export function readTrainerStatus(payload) { return pickStr(payload, ['status']); }
export function readTrainerApplicationId(payload) { return pickStr(payload, ['application_id']); }
export function readTrainerFirstName(payload) { return pickStr(payload, ['first_name']); }
export function readTrainerLastName(payload) { return pickStr(payload, ['last_name']); }
export function readTrainerEmail(payload) { return pickStr(payload, ['email']); }
export function readTrainerPhone(payload) { return pickStr(payload, ['phone']); }
export function readTrainerBusinessName(payload) { return pickStr(payload, ['business_name']); }
export function readTrainerTrainingSpecialty(payload) { return pickStr(payload, ['training_specialty']); }
export function readTrainerLocation(payload) { return pickStr(payload, ['location']); }
export function readTrainerWebsiteOrSocial(payload) { return pickStr(payload, ['website_or_social']); }
export function readTrainerBio(payload) { return pickStr(payload, ['bio']); }
export function readTrainerCertificationCredentials(payload) { return pickStr(payload, ['certification_credentials']); }
export function readTrainerApproximateActiveClients(payload) { return pickNum(payload, ['approximate_active_clients']); }
export function readTrainerRequestedCode(payload) { return pickStr(payload, ['requested_trainer_code']); }
export function readTrainerCurrentCode(payload) { return pickStr(payload, ['current_code']); }
export function readTrainerApprovedCode(payload) { return pickStr(payload, ['approved_code']); }
export function readTrainerCodeValidFrom(payload) { return pickStr(payload, ['current_code_valid_from']); }
export function readTrainerCodeExpiresAt(payload) { return pickStr(payload, ['current_code_expires_at']); }
export function readTrainerActive(payload) { return pickBool(payload, ['trainer_active']); }
export function readTrainerApprovedAt(payload) { return pickStr(payload, ['approved_at']); }
export function readTrainerId(payload) { return pickStr(payload, ['trainer_id']); }
export function readTrainerTermsVersion(payload) { return pickStr(payload, ['terms_version']); }
export function readTrainerProgramTermsAccepted(payload) { return pickBool(payload, ['program_terms_accepted']); }
export function readTrainerDiscountPercent(payload) { return pickNum(payload, ['discount_percent']); }
export function readTrainerMinimumCoreMeals(payload) { return pickNum(payload, ['minimum_core_meals']); }
export function readTrainerMaxDiscountedCoreMeals(payload) { return pickNum(payload, ['max_discounted_core_meals']); }

export function hasTrainerApplication(payload) {
  return readTrainerOutcome(payload) !== 'no_application';
}

// Program terms readers.
export function readTermsBody(payload) { return pickStr(payload, ['body']); }
export function readTermsTitle(payload) { return pickStr(payload, ['title']); }
export function readTermsVersion(payload) { return pickStr(payload, ['version']); }
export function readTermsCheckboxLabel(payload) { return pickStr(payload, ['checkbox_label']); }
export function readTermsEffectiveAt(payload) { return pickStr(payload, ['effective_at']); }

// Success-outcome tests for customer-facing status branching.
export function isSubmitSuccessOutcome(outcome) {
  return outcome === 'submitted' || outcome === 'application_exists';
}
export function isUpdateSuccessOutcome(outcome) {
  return outcome === 'updated' || outcome === 'no_changes';
}

// Referral activity readers. React never fabricates a row/summary
// value that the RPC did not return.
function pickArr(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (Array.isArray(v)) return v; } return null; }

export function readReferralOutcome(payload) { return pickStr(payload, ['outcome']); }
export function readReferralStartAt(payload) { return pickStr(payload, ['start_at']); }
export function readReferralEndAt(payload) { return pickStr(payload, ['end_at']); }
export function readReferralSummaryOrders(payload) { return pickNum(payload?.summary, ['orders']); }
export function readReferralSummaryMealsSold(payload) { return pickNum(payload?.summary, ['meals_sold']); }

export function readReferralActivityRows(payload) {
  const arr = pickArr(payload, ['activity']) || [];
  return arr.map((raw) => ({
    client_name: pickStr(raw, ['client_name']),
    trainer_code: pickStr(raw, ['trainer_code']),
    order_reference: pickStr(raw, ['order_reference']),
    order_at: pickStr(raw, ['order_at']),
    order_status: pickStr(raw, ['order_status']),
    meals_sold: pickNum(raw, ['meals_sold']),
  }));
}

// My Clients readers (get_my_trainer_clients). React never fabricates
// a value the RPC did not return; null purchase fields are legitimate
// for a currently-attributed client with no qualifying purchase yet.
export function readClientsOutcome(payload) { return pickStr(payload, ['outcome']); }
export function readClientsCurrentCount(payload) { return pickNum(payload?.summary, ['current_clients']); }

export function readClientsRows(payload) {
  const arr = pickArr(payload, ['clients']) || [];
  return arr.map((raw) => ({
    client_name: pickStr(raw, ['client_name']),
    attributed_at: pickStr(raw, ['attributed_at']),
    trainer_code: pickStr(raw, ['trainer_code']),
    first_qualifying_purchase_at: pickStr(raw, ['first_qualifying_purchase_at']),
    most_recent_qualifying_purchase_at: pickStr(raw, ['most_recent_qualifying_purchase_at']),
    qualifying_order_count: pickNum(raw, ['qualifying_order_count']),
    total_meals_purchased: pickNum(raw, ['total_meals_purchased']),
    most_recent_qualifying_order_status: pickStr(raw, ['most_recent_qualifying_order_status']),
  }));
}
